import React, { memo, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { Comment } from 'services/apiTypes/comment.types'
import CommentService from 'services/comment.service'
import ToastService from 'services/toast.service'

import Thread, { updateCommentChain, removeUserInputs } from 'components/comments/Thread'
import Editor from 'components/formElements/richText/Editor'
import LineLoader from 'components/elements/LineLoader'

import { emptyTextArea } from 'types/editor'

import { useAppSelector } from 'redux/hooks'

import styles from './commentContainer.module.scss'

const CommentContainer = memo(() => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const commentId = useAppSelector(state => state.deck.commentid)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [comment, setComment] = useState(emptyTextArea)
  const [deckThread, setDeckThread] = useState<Comment>()

  useEffect(() => {
    if (!commentId) return

    CommentService.details(commentId)
      .then(setDeckThread)
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (userId) return
    if (!deckThread) return

    const updatedThread = removeUserInputs(deckThread)

    setDeckThread(updatedThread)
  }, [userId])

  const handleUpdateDeckThread = (updatedComment: Comment) => {
    if (!deckThread) return

    const updatedRootThread = updateCommentChain(deckThread, updatedComment)

    setDeckThread(updatedRootThread)
  }

  const handleSubmitComment = () => {
    if (!deckThread) return Promise.resolve()

    return CommentService.createComment(deckThread.id, comment)
      .then(newComment => {
        const updatedComment: Comment = {
          ...deckThread,
          children: [newComment, ...deckThread.children],
          childrenCount: deckThread.childrenCount + 1,
        }

        setComment(emptyTextArea)
        setDeckThread(updatedComment)
      })
      .catch(() => ToastService.create('Unable to create comment'))
  }

  if (!commentId) return null // Used when viewing a snapshot (there are no comments on snapshots)
  if (error || !deckThread) return <div className={styles.error}>Unable to load comments</div>

  if (loading)
    return (
      <div>
        <h2>Leave a comment</h2>
        <Editor placeholder="Leave a comment" />
        <LineLoader />
      </div>
    )

  // Rendering a thread with a custom child replaces the default root comment being rendered
  // This is useful for a deck where the root comment is always empty (auto generated per deck)
  return (
    <Thread
      rootPageComment
      noCollapse
      noPaginatorView
      comment={deckThread}
      onCommentUpdate={handleUpdateDeckThread}
      className={styles.container}
      archived={deckThread.archived}>
      <h2>Leave a comment</h2>
      <Editor onChange={setComment} value={comment} placeholder="Leave a comment" onSubmit={handleSubmitComment} />
    </Thread>
  )
})

export default CommentContainer
